import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import tokenManager from '../../services/tokenManager'
import { 
  BarChart3, 
  TrendingUp, 
  BugIcon, 
  Settings,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  User,
  LogOut
} from 'lucide-react'

/**
 * Responsive sidebar component that adapts to screen size
 * Collapsible on mobile, always visible on desktop
 * Can be configured for full-height app layout or traditional layout
 * Supports mobile overlay mode for application pages
 */
const Sidebar = ({ 
  fullHeight = false, 
  isCollapsed = false, 
  onToggle,
  isMobileOpen = false,
  onMobileToggle
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false)
  const location = useLocation()
  const { isAuthenticated, user, logout, isLoading } = useAuth0()
  
  // Use external state if provided, otherwise use internal state
  const collapsed = onToggle ? isCollapsed : internalCollapsed
  const setCollapsed = onToggle ? onToggle : setInternalCollapsed

  const handleLogout = async () => {
    try {
      // Clear cached token
      tokenManager.clearToken()
      
      await logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      })
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  // Handle opening Sentry feedback dialog for Help button
  const handleHelpClick = async () => {
    try {
      // Open Sentry feedback dialog programmatically
      // Get the instance returned by `feedbackIntegration()`
      const feedback = Sentry.getFeedback();
      const form = await feedback?.createForm();
      form.appendToDom();
      form.open();
    } catch (error) {
      console.error('Failed to open feedback dialog:', error)
    }
  }

  const navigationItems = [
    { name: 'Dashboard', path: '/dashboard', icon: BarChart3 },
    { name: 'Portfolio', path: '/portfolio', icon: TrendingUp },
    // { name: 'Documents', path: '/documents', icon: FileText },
    { name: 'Settings', path: '/settings', icon: Settings },
    { name: 'Help', path: null, icon: BugIcon, isAction: true }, // Special action item for Sentry feedback
    // { name: 'Deals', path: '/deals', icon: Target },
  ]

  const isActive = (path) => location.pathname === path

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
          className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 md:hidden ${
            isMobileOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={onMobileToggle}
        />
      <div className={`md:block fixed left-0 top-0 h-screen z-40 
            w-60 z-50 transform transition-transform duration-300 ease-in-out
            ${
              isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
            } ${
              collapsed ? 'md:w-16 lg:w-14' : ''
            }`}>
        <div className={`bg-zinc-800 transition-all duration-300 h-full w-60 ${
          collapsed ? 'md:w-16 lg:w-14' : ''
        }`}>
          <div className="flex flex-col h-full">
            {/* Top Section with Brand and Navigation */}
            <div className="flex-1">
              <div className={`p-2`}>
                {/* Brand Identity Section */}
                <div className={`mb-8 ${collapsed ? 'md:mb-6' : ''}`} onClick={onMobileToggle}>
                  <Link to="/dashboard" className={`flex items-center space-x-3 ${collapsed ? 'md:justify-center' : ''} `}>
                    <div className={`m-3 ${collapsed ? 'md:m-2' : ''} w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <img
                        src="https://startup-tnt-documents.tor1.digitaloceanspaces.com/public/startuptnt-icon.svg"
                        alt="Startup TNT Icon"
                        className="h-7 w-7"
                      />
                    </div>
                    <div className={`flex flex-col min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
                      <span className="text-white text-xl font-bold truncate">Startup TNT</span>
                      <span className="text-gray-300 text-sm truncate">Angel Vault</span>
                    </div>
                  </Link>
                </div>
              
                {/* Navigation items */}
                <nav className="space-y-2">
                  {navigationItems.map((item) => {
                    // Handle Help button as action instead of navigation
                    if (item.isAction) {
                      return (
                        <button
                          key={item.name}
                          onClick={() => {
                            handleHelpClick()
                            onMobileToggle?.() // Close mobile menu if open
                          }}
                          className={`w-full flex items-center px-4 py-3 text-sm font-medium transition-colors rounded-lg text-white hover:bg-tnt-default ${collapsed ? 'md:justify-center' : ''}`}
                        >
                          <item.icon className={`w-5 h-5 mr-3 ${collapsed ? 'md:mr-0' : ''} flex-shrink-0`} />
                          <span className={`truncate ${collapsed ? 'md:hidden' : ''}`}>{item.name}</span>
                        </button>
                      )
                    }
                    
                    // Regular navigation items
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center px-4 py-3 text-sm font-medium transition-colors rounded-lg ${
                          isActive(item.path)
                            ? 'bg-tnt-default text-white'
                            : 'text-white hover:bg-tnt-default'
                        } ${collapsed ? 'md:justify-center' : ''}`}
                        onClick={onMobileToggle}
                      >
                        <item.icon className={`w-5 h-5 mr-3 ${collapsed ? 'md:mr-0' : ''} flex-shrink-0`} />
                        <span className={`truncate ${collapsed ? 'md:hidden' : ''}`}>{item.name}</span>
                      </Link>
                    )
                  })}
                </nav>
              </div>
            </div>

            {/* User Authentication Section */}
            {isAuthenticated && (
                <div className="p-2 border-t bg-tnt-hover md:hidden">
                  {/* User info with avatar */}
                  <div className="flex items-center space-x-3 px-4 py-3 text-sm text-white mb-2">
                    {/* Auth0 user avatar or default user icon */}
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt="User avatar"
                        className="h-8 w-8 rounded-full"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{user?.name || 'User'}</p>
                      <p className="text-xs text-gray-300 truncate">{user?.email}</p>
                    </div>
                  </div>
                  
                  {/* Sign out button */}
                  <button
                    onClick={handleLogout}
                    disabled={isLoading}
                    className="w-full px-5 py-3 rounded-md hover:bg-tnt-hover text-white transition-colors flex items-center disabled:opacity-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}

            {/* Bottom Section with Toggle Button */}
            <div className={`border-t border-zinc-400 hidden md:block`}>
              <button
                onClick={() => setCollapsed(!collapsed)}
                className={`w-full px-4 py-3 rounded-md hover:bg-tnt-hover text-white transition-colors ${
                  collapsed ? 'flex justify-center' : ''
                }`}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar
